/**
 * 简化的地区管理系统
 * 主要依赖 app-startup-initializer.js 的检测结果
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export type RegionType = 'cn' | 'global';

// 后端 API 配置 - 从环境变量读取，提供默认值作为后备
const BACKEND_URLS = {
  cn: import.meta.env.VITE_BACKEND_API_URL_CN || 'https://tempo-0y6p.onrender.com/',
  global: import.meta.env.VITE_BACKEND_API_URL_GLOBAL || 'https://tempo-0y6p.onrender.com/'
};

// 前端域名配置 - 从环境变量读取，提供默认值作为后备
const FRONTEND_URLS = {
  cn: import.meta.env.VITE_PRODUCTION_DOMAIN_CN || 'https://tempo.lengditech.com',
  global: import.meta.env.VITE_PRODUCTION_DOMAIN_GLOBAL || 'https://tempo.motiful.ai'
};



/**
 * 获取当前地区（多重策略）
 */
export const getCurrentRegion = (): RegionType => {
  // 1. 优先使用全局变量（由 app-startup-initializer.js 设置）
  try {
    const globalRegion = (window as any).TEMPO_DETECTED_REGION as RegionType;
    if (globalRegion === 'cn' || globalRegion === 'global') {
      return globalRegion;
    }
  } catch (error) {
    // 忽略错误
  }

  // 2. 根据当前域名判断
  try {
    const hostname = window.location.hostname;
    if (hostname.includes('lengditech.com')) {
      return 'cn';
    }
  } catch (error) {
    // 忽略错误
  }

  // 3. 默认为全球版（移除不准确的语言判断）
  return 'global';
};

/**
 * 当前地区 Atom - 使用 localStorage 持久化
 */
export const currentRegionAtom = atomWithStorage<RegionType>('tempo-user-region', getCurrentRegion());

/**
 * 地区相关 URL 配置 Atom
 */
export const regionUrlsAtom = atom((get) => {
  const region = get(currentRegionAtom);
  return {
    region,
    backendAPI: BACKEND_URLS[region],
    frontendDomain: FRONTEND_URLS[region]
  };
});

/**
 * 获取后端 API URL 的便捷函数
 * 注意：这个函数支持环境变量覆盖，与 apiClient.ts 中的 getApiBaseUrl 不同
 */
export const getBackendApiUrl = (region?: RegionType): string => {
  // 优先检查本地开发覆盖环境变量（无视地区）
  if (import.meta.env.VITE_BACKEND_API_URL_LOCAL) {
    return import.meta.env.VITE_BACKEND_API_URL_LOCAL;
  }

  let targetRegion = region;

  if (!targetRegion) {
    // 如果没有指定地区，尝试从 localStorage 读取
    try {
      targetRegion = localStorage.getItem('tempo-user-region') as RegionType;
      if (targetRegion !== 'cn' && targetRegion !== 'global') {
        targetRegion = getCurrentRegion();
      }
    } catch {
      targetRegion = getCurrentRegion();
    }
  }

  // 支持环境变量覆盖后端 API URL
  if (targetRegion === 'cn') {
    return import.meta.env.VITE_BACKEND_API_URL_CN || BACKEND_URLS.cn;
  } else {
    return import.meta.env.VITE_BACKEND_API_URL_GLOBAL || BACKEND_URLS.global;
  }
};

/**
 * 获取前端域名的便捷函数
 */
export const getProductionDomain = (region?: RegionType): string => {
  let targetRegion = region;

  if (!targetRegion) {
    // 如果没有指定地区，尝试从 localStorage 读取
    try {
      targetRegion = localStorage.getItem('tempo-user-region') as RegionType;
      if (targetRegion !== 'cn' && targetRegion !== 'global') {
        targetRegion = getCurrentRegion();
      }
    } catch {
      targetRegion = getCurrentRegion();
    }
  }

  // 支持环境变量覆盖前端域名
  if (targetRegion === 'cn') {
    return import.meta.env.VITE_PRODUCTION_DOMAIN_CN || FRONTEND_URLS.cn;
  } else {
    return import.meta.env.VITE_PRODUCTION_DOMAIN_GLOBAL || FRONTEND_URLS.global;
  }
};

/**
 * 构建分享链接
 */
export const buildShareUrl = (path: string, region?: RegionType): string => {
  const domain = getProductionDomain(region);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${domain}${cleanPath}`;
};

/**
 * 判断是否为中国域名
 */
export const isChineseDomain = (domain: string): boolean => {
  return domain.includes('lengditech.com');
};


